import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import DeleteUserConfirmationModal from '../components/DeleteUserConfirmationModal';
import AdminRightsManager from '../components/admin/AdminRightsManager';
import ProgramsAdmin from '../components/admin/ProgramsAdmin';
import { AdminService, FeedbackItem, UserMetrics, UserInfo, UserStatusUpdate } from '../services/adminService';
import { apiService } from '../services/api';
import { DEFAULT_SITE_CONFIG } from '../services/siteConfigService';
import styles from '../styles/admin.module.css';

interface User {
  name: string;
  email: string;
  isAdmin?: boolean;
}

interface AdminProps {
  user: User | null;
  onLogout: () => void;
  onLogin: () => void;
}

const Admin: React.FC<AdminProps> = ({ user, onLogout, onLogin }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'home' | 'programs' | 'feedback' | 'users' | 'metrics'>('home');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialLoad, setInitialLoad] = useState(true);
  
  // Data states
  const [feedback, setFeedback] = useState<FeedbackItem[]>([]);
  const [metrics, setMetrics] = useState<UserMetrics | null>(null);
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<{ email: string; name?: string } | null>(null);
  
  // Admin rights management
  const [showRightsManager, setShowRightsManager] = useState(false);
  const [userForRightsEdit, setUserForRightsEdit] = useState<UserInfo | null>(null);
  
  // User filter states - grouping and combined status filter
  const [groupBy, setGroupBy] = useState<'login' | 'registration' | 'nothing'>('login');
  const [statusFilter, setStatusFilter] = useState<'active_verified' | 'not_verified' | 'inactive'>('active_verified');
  
  // Feedback filter states
  const [feedbackTypeFilter, setFeedbackTypeFilter] = useState<'any' | 'general' | 'idea' | 'error'>('any');
  const [feedbackReviewFilter, setFeedbackReviewFilter] = useState<'not_reviewed' | 'reviewed' | 'all'>('not_reviewed');

  // Home page (site) config
  const [siteConfig, setSiteConfig] = useState({
    tagline: DEFAULT_SITE_CONFIG.tagline,
    section_heading: DEFAULT_SITE_CONFIG.section_heading,
    bullet1: DEFAULT_SITE_CONFIG.bullet1,
    bullet2: DEFAULT_SITE_CONFIG.bullet2,
    bullet3: DEFAULT_SITE_CONFIG.bullet3,
    meetings_enabled: DEFAULT_SITE_CONFIG.meetings_enabled,
  });
  const [siteConfigLoading, setSiteConfigLoading] = useState(true);
  const [siteConfigSaving, setSiteConfigSaving] = useState(false);
  const [siteConfigMessage, setSiteConfigMessage] = useState<string | null>(null);

  // Load site config on mount (for admin home page section)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setSiteConfigLoading(true);
        const config = await AdminService.getSiteConfig();
        if (!cancelled) {
          setSiteConfig({
            tagline: config.tagline ?? DEFAULT_SITE_CONFIG.tagline,
            section_heading: config.section_heading ?? DEFAULT_SITE_CONFIG.section_heading,
            bullet1: config.bullet1 ?? DEFAULT_SITE_CONFIG.bullet1,
            bullet2: config.bullet2 ?? DEFAULT_SITE_CONFIG.bullet2,
            bullet3: config.bullet3 ?? DEFAULT_SITE_CONFIG.bullet3,
            meetings_enabled: config.meetings_enabled ?? DEFAULT_SITE_CONFIG.meetings_enabled,
          });
        }
      } catch {
        if (!cancelled) {
          setSiteConfig({
            tagline: DEFAULT_SITE_CONFIG.tagline,
            section_heading: DEFAULT_SITE_CONFIG.section_heading,
            bullet1: DEFAULT_SITE_CONFIG.bullet1,
            bullet2: DEFAULT_SITE_CONFIG.bullet2,
            bullet3: DEFAULT_SITE_CONFIG.bullet3,
            meetings_enabled: DEFAULT_SITE_CONFIG.meetings_enabled,
          });
        }
      } finally {
        if (!cancelled) setSiteConfigLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSaveSiteConfig = async () => {
    try {
      setSiteConfigSaving(true);
      setSiteConfigMessage(null);
      await AdminService.updateSiteConfig(siteConfig);
      setSiteConfigMessage('Saved.');
      setTimeout(() => setSiteConfigMessage(null), 3000);
    } catch (e) {
      setSiteConfigMessage('Failed to save.');
    } finally {
      setSiteConfigSaving(false);
    }
  };

  // Load users data when component mounts (default tab)
  useEffect(() => {
    if (user?.isAdmin && activeTab === 'users' && users.length === 0) {
      loadUsers();
    } else if (user?.isAdmin && activeTab === 'feedback' && feedback.length === 0) {
      loadFeedback();
    }
  }, [activeTab, user?.isAdmin]);

  // Redirect if not admin - moved to useEffect to avoid render-time navigation
  useEffect(() => {
    if (!user?.isAdmin) {
      navigate('/');
    }
  }, [user?.isAdmin, navigate]);

  // Don't render anything if not admin
  if (!user?.isAdmin) {
    return null;
  }

  const breadcrumbs = [
    { label: 'Home', path: '/', active: false },
    { label: 'Admin', path: '/admin', active: true }
  ];


  const loadFeedback = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await AdminService.getFeedback();
      setFeedback(data);
    } catch (err) {
      setError('Failed to load feedback data');
      console.error('Error loading feedback:', err);
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  };

  const loadMetrics = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await AdminService.getMetrics();
      setMetrics(data);
    } catch (err) {
      setError('Failed to load metrics data');
      console.error('Error loading metrics:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await AdminService.getUsers();
      setUsers(data);
    } catch (err) {
      setError('Failed to load users data');
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab: 'home' | 'programs' | 'feedback' | 'users' | 'metrics') => {
    setActiveTab(tab);
    setError(null);
    
    // Load data for the selected tab
    if (tab === 'metrics' && !metrics) {
      loadMetrics();
    } else if (tab === 'users' && users.length === 0) {
      loadUsers();
    }
    // Programs tab will load its own data
  };

  const handleManageRights = (userInfo: UserInfo) => {
    setUserForRightsEdit(userInfo);
    setShowRightsManager(true);
  };

  const handleRightsSave = async () => {
    setShowRightsManager(false);
    setUserForRightsEdit(null);
    await loadUsers(); // Reload users to show updated rights
  };

  const handleRightsCancel = () => {
    setShowRightsManager(false);
    setUserForRightsEdit(null);
  };

  const handleMarkReviewed = async (feedbackId: string) => {
    try {
      setLoading(true);
      await AdminService.markFeedbackReviewed(feedbackId);
      // Reload feedback to get updated data
      await loadFeedback();
    } catch (err) {
      setError('Failed to mark feedback as reviewed');
      console.error('Error marking feedback as reviewed:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExportFeedback = async () => {
    try {
      const csvData = await AdminService.exportFeedback();
      const blob = new Blob([csvData], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `feedback-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Failed to export feedback');
      console.error('Error exporting feedback:', err);
    }
  };

  const handleUpdateUserStatus = async (userId: string, status: 'active' | 'inactive' | 'test', reason?: string) => {
    try {
      setLoading(true);
      await AdminService.updateUserStatus(userId, status, reason);
      
      // Reload users to get updated data
      await loadUsers();
      
      // Reload metrics if on metrics tab to reflect changes
      if (activeTab === 'metrics') {
        await loadMetrics();
      }
    } catch (err) {
      setError('Failed to update user status');
      console.error('Error updating user status:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUserClick = (userEmail: string, userName?: string) => {
    setUserToDelete({ email: userEmail, name: userName });
    setDeleteModalOpen(true);
  };

  const handleDeleteUserConfirm = async () => {
    if (!userToDelete) return;

    try {
      setLoading(true);
      setDeleteModalOpen(false);
      await AdminService.deleteUser(userToDelete.email);
      
      // Reload users to get updated data
      await loadUsers();
      
      // Reload metrics if on metrics tab to reflect changes
      if (activeTab === 'metrics') {
        await loadMetrics();
      }
      
      setUserToDelete(null);
    } catch (err) {
      setError('Failed to delete user');
      console.error('Error deleting user:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUserCancel = () => {
    setDeleteModalOpen(false);
    setUserToDelete(null);
  };

  const handleUpdateUserRole = async (userId: string, role: 'user' | 'admin') => {
    try {
      setLoading(true);
      await AdminService.updateUserRole(userId, role);
      
      // Reload users to get updated data
      await loadUsers();
      
      // Reload metrics if on metrics tab to reflect changes
      if (activeTab === 'metrics') {
        await loadMetrics();
      }
    } catch (err) {
      setError('Failed to update user role');
      console.error('Error updating user role:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short'
    });
  };

  const formatDeviceInfo = (deviceInfo: any) => {
    if (!deviceInfo) return 'N/A';
    return `${deviceInfo.deviceType || 'Unknown'} - ${deviceInfo.browser || 'Unknown'} ${deviceInfo.browserVersion || ''} on ${deviceInfo.operatingSystem || 'Unknown'}`;
  };

  const renderFeedbackTab = () => {
    // Apply feedback filters
    const filteredFeedback = feedback.filter(item => {
      // Type filter
      if (feedbackTypeFilter !== 'any' && item.type !== feedbackTypeFilter) {
        return false;
      }
      
      // Review status filter
      if (feedbackReviewFilter === 'not_reviewed' && item.reviewed) {
        return false;
      }
      if (feedbackReviewFilter === 'reviewed' && !item.reviewed) {
        return false;
      }
      
      return true;
    });

    return (
      <div className={styles.tabContent}>
        <div className={styles.tabHeader}>
          <h2>Feedback Management</h2>
          <button 
            onClick={handleExportFeedback}
            className={styles.exportButton}
            disabled={loading}
          >
            Export CSV
          </button>
        </div>
        
        {/* Filter Controls */}
        <div className={styles.filtersContainer}>
          <div className={styles.filterRow}>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Type:</label>
              <select 
                value={feedbackTypeFilter} 
                onChange={(e) => setFeedbackTypeFilter(e.target.value as any)}
                className={styles.filterSelect}
              >
                <option value="any">Any</option>
                <option value="general">General</option>
                <option value="idea">Idea</option>
                <option value="error">Error/Bug</option>
              </select>
            </div>
            
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Review Status:</label>
              <select 
                value={feedbackReviewFilter} 
                onChange={(e) => setFeedbackReviewFilter(e.target.value as any)}
                className={styles.filterSelect}
              >
                <option value="not_reviewed">Not Reviewed</option>
                <option value="reviewed">Reviewed</option>
                <option value="all">All</option>
              </select>
            </div>
            
            <div className={styles.userCount}>
              {filteredFeedback.length} {filteredFeedback.length === 1 ? 'item' : 'items'}
            </div>
          </div>
        </div>
        
        {error && <div className={styles.error}>{error}</div>}
        
        {loading && initialLoad ? (
          <div className={styles.loading}>Loading feedback...</div>
        ) : loading ? (
          <div className={styles.loading}>Refreshing feedback...</div>
        ) : filteredFeedback.length === 0 ? (
          <div className={styles.emptyState}>No feedback matches the selected filters.</div>
        ) : (
          <div className={styles.feedbackList}>
            {filteredFeedback.map((item) => (
            <div key={item.id} className={`${styles.feedbackItem} ${item.reviewed ? styles.reviewed : ''}`}>
              <div className={styles.feedbackHeader}>
                <div className={styles.feedbackMeta}>
                  <div className={styles.feedbackPrimary}>
                    <span className={styles.userEmail}>{item.userEmail}</span>
                    <span className={styles.timestamp}>{formatDate(item.timestamp)}</span>
                  </div>
                  <div className={styles.feedbackSecondary}>
                    <span className={styles.location}>{item.location}</span>
                    <span className={styles.type}>{item.type}</span>
                  </div>
                  <div className={styles.deviceInfo}>
                    {formatDeviceInfo(item.deviceInfo)}
                  </div>
                </div>
                <div className={styles.feedbackActions}>
                  {!item.reviewed && (
                    <button
                      onClick={() => handleMarkReviewed(item.id)}
                      className={styles.reviewButton}
                      disabled={loading}
                    >
                      {loading ? 'Marking...' : 'Mark Reviewed'}
                    </button>
                  )}
                  {item.reviewed && (
                    <div className={styles.reviewedBadge}>✓ Reviewed</div>
                  )}
                </div>
              </div>
              <div className={styles.feedbackText}>{item.feedback}</div>
            </div>
          ))}
        </div>
      )}
    </div>
    );
  };

  const renderMetricsTab = () => (
    <div className={styles.tabContent}>
      <h2>Usage Metrics</h2>
      
      {error && <div className={styles.error}>{error}</div>}
      
      {loading ? (
        <div className={styles.loading}>Loading metrics...</div>
      ) : !metrics ? (
        <div className={styles.emptyState}>No metrics data available.</div>
      ) : (
        <div className={styles.metricsContainer}>
          {/* User Overview Section */}
          <div className={styles.metricsSection}>
            <h3>User Overview</h3>
            <div className={styles.metricsGrid}>
              <div className={styles.metricCard}>
                <h4>Total Users</h4>
                <div className={styles.metricValue}>{metrics.totalUsers}</div>
              </div>
              <div className={styles.metricCard}>
                <h4>Active Users (30 days)</h4>
                <div className={styles.metricValue}>{metrics.activeUsers}</div>
                <div className={styles.metricSubtext}>
                  {metrics.totalUsers > 0 ? `${Math.round((metrics.activeUsers / metrics.totalUsers) * 100)}% of total` : '0%'}
                </div>
              </div>
              <div className={styles.metricCard}>
                <h4>New Users (7 days)</h4>
                <div className={styles.metricValue}>{metrics.newUsersThisWeek}</div>
              </div>
              <div className={styles.metricCard}>
                <h4>New Users (30 days)</h4>
                <div className={styles.metricValue}>{metrics.newUsersThisMonth}</div>
              </div>
              <div className={styles.metricCard}>
                <h4>Retention Rate</h4>
                <div className={styles.metricValue}>{metrics.retentionRate}%</div>
                <div className={styles.metricSubtext}>
                  {metrics.returnUsers} return users
                </div>
              </div>
              <div className={styles.metricCard}>
                <h4>Program Participants</h4>
                <div className={styles.metricValue}>{metrics.usersWithProgress}</div>
                <div className={styles.metricSubtext}>
                  Users who have started the program
                </div>
              </div>
            </div>
          </div>

          {/* Step Analytics Section */}
          <div className={styles.metricsSection}>
            <h3>Step Analytics</h3>
            <div className={styles.stepAnalysisGrid}>
              {Object.entries(metrics.stepAnalytics).map(([step, analytics]) => {
                const stepNum = step.replace('step_', '');
                return (
                  <div key={step} className={styles.stepAnalysisCard}>
                    <h4>Step {stepNum}</h4>
                    <div className={styles.stepMetrics}>
                      <div className={styles.stepMetric}>
                        <span className={styles.metricLabel}>Started:</span>
                        <span className={styles.metricValue}>{analytics.users_started}</span>
                      </div>
                      <div className={styles.stepMetric}>
                        <span className={styles.metricLabel}>In Progress:</span>
                        <span className={styles.metricValue}>{analytics.users_in_progress}</span>
                      </div>
                      <div className={styles.stepMetric}>
                        <span className={styles.metricLabel}>Completed:</span>
                        <span className={styles.metricValue}>{analytics.users_completed}</span>
                      </div>
                      <div className={styles.stepMetric}>
                        <span className={styles.metricLabel}>Avg Days:</span>
                        <span className={styles.metricValue}>{analytics.average_days}</span>
                      </div>
                    </div>
                    <div className={styles.progressBar}>
                      <div 
                        className={styles.progressFill} 
                        style={{ 
                          width: `${analytics.users_started > 0 ? (analytics.users_completed / analytics.users_started) * 100 : 0}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Step Four Inventory Analytics Section */}
          <div className={styles.metricsSection}>
            <h3>Step Four Inventory Completion</h3>
            <div className={styles.inventoryGrid}>
              <div className={styles.metricCard}>
                <h4>All Inventories Completed</h4>
                <div className={styles.metricValue}>{metrics.usersCompletedAllInventories}</div>
                <div className={styles.metricSubtext}>
                  {metrics.allInventoriesCompletedRate}% of all users • {metrics.allInventoriesCompletedRateStep4}% of Step 4 users
                </div>
              </div>
              {Object.entries(metrics.inventoryAnalytics).map(([type, analytics]) => (
                <div key={type} className={styles.inventoryCard}>
                  <h4>{type.charAt(0).toUpperCase() + type.slice(1)}</h4>
                  <div className={styles.inventoryMetrics}>
                    <div className={styles.inventoryMetric}>
                      <span className={styles.metricLabel}>Started:</span>
                      <span className={styles.metricValue}>{analytics.users_started}</span>
                    </div>
                    <div className={styles.inventoryMetric}>
                      <span className={styles.metricLabel}>Completed:</span>
                      <span className={styles.metricValue}>{analytics.users_completed}</span>
                    </div>
                    <div className={styles.inventoryMetric}>
                      <span className={styles.metricLabel}>Avg Entries:</span>
                      <span className={styles.metricValue}>{analytics.avg_entries}</span>
                    </div>
                    <div className={styles.inventoryMetric}>
                      <span className={styles.metricLabel}>Max Entries:</span>
                      <span className={styles.metricValue}>{analytics.max_entries}</span>
                    </div>
                  </div>
                  <div className={styles.progressBar}>
                    <div 
                      className={styles.progressFill} 
                      style={{ 
                        width: `${analytics.users_started > 0 ? (analytics.users_completed / analytics.users_started) * 100 : 0}%` 
                      }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderUsersTab = () => {
    // Convert date to Pacific time and normalize to start of day
    const toPacificDate = (dateString: string | undefined): Date | null => {
      if (!dateString) return null;
      const date = new Date(dateString);
      const pacificDateStr = date.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' });
      const pacificDate = new Date(pacificDateStr);
      // Normalize to start of day
      return new Date(pacificDate.getFullYear(), pacificDate.getMonth(), pacificDate.getDate());
    };

    // Get current Pacific date normalized to start of day
    const getPacificNow = (): Date => {
      const now = new Date();
      const pacificNowStr = now.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' });
      const pacificNow = new Date(pacificNowStr);
      return new Date(pacificNow.getFullYear(), pacificNow.getMonth(), pacificNow.getDate());
    };

    // Get the grouping key and label based on days ago
    const getGroupInfo = (date: Date): { key: string; label: string; sortOrder: number } => {
      const now = getPacificNow();
      const diffMs = now.getTime() - date.getTime();
      const daysAgo = Math.round(diffMs / (1000 * 60 * 60 * 24));

      // Group 1: Today (0 days ago)
      if (daysAgo === 0) {
        const label = `Today, ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
        return { key: `001-today`, label, sortOrder: 1 };
      } 
      
      // Group 2: Yesterday (1 day ago)
      else if (daysAgo === 1) {
        const label = `Yesterday, ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
        return { key: `002-yesterday`, label, sortOrder: 2 };
      } 
      
      // Group 3: The week prior (2-8 days ago = 7 days before yesterday)
      else if (daysAgo >= 2 && daysAgo <= 8) {
        // Calculate the week range: from 8 days ago to 2 days ago
        const weekEnd = new Date(now);
        weekEnd.setDate(now.getDate() - 2);
        const weekStart = new Date(now);
        weekStart.setDate(now.getDate() - 8);
        
        const startMonth = weekStart.toLocaleDateString('en-US', { month: 'long' });
        const startDay = weekStart.getDate();
        const endMonth = weekEnd.toLocaleDateString('en-US', { month: 'long' });
        const endDay = weekEnd.getDate();
        const year = weekEnd.getFullYear();
        
        const label = startMonth === endMonth 
          ? `${startMonth} ${startDay}-${endDay}, ${year}`
          : `${startMonth} ${startDay}-${endMonth} ${endDay}, ${year}`;
        
        return { key: `003-week-prior`, label, sortOrder: 3 };
      } 
      
      // Group 4+: Monthly grouping (9+ days ago)
      else {
        const month = date.toLocaleDateString('en-US', { month: 'long' });
        const year = date.getFullYear();
        const label = `${month} ${year}`;
        
        // Sort order: current month is 4, then 5, 6, 7... going back in time
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth();
        const dateYear = date.getFullYear();
        const dateMonth = date.getMonth();
        const monthsAgo = (currentYear - dateYear) * 12 + (currentMonth - dateMonth);
        const sortOrder = 4 + monthsAgo;
        
        const key = `${String(sortOrder).padStart(3, '0')}-month-${year}-${dateMonth}`;
        return { key, label, sortOrder };
      }
    };

    // Apply status filter
    const filteredUsers = users.filter(user => {
      const isActive = (user.status || 'active') === 'active';
      const isVerified = user.emailVerified;
      
      if (statusFilter === 'active_verified') {
        // Show only users that are BOTH active AND verified
        return isActive && isVerified;
      } else if (statusFilter === 'not_verified') {
        // Show all unverified users (regardless of active status)
        return !isVerified;
      } else if (statusFilter === 'inactive') {
        // Show all inactive users (regardless of verified status)
        return !isActive;
      }
      
      return true;
    });

    // Sort and optionally group users
    let sortedUsers: any[] = [];
    let groupedUsersArray: Array<{ key: string; label: string; users: any[] }> = [];

    if (groupBy === 'nothing') {
      // No grouping - just sort by most recent login
      sortedUsers = [...filteredUsers].sort((a, b) => {
        const dateA = new Date(a.lastLogin || 0).getTime();
        const dateB = new Date(b.lastLogin || 0).getTime();
        return dateB - dateA;
      });
    } else {
      // Group by selected field
      const dateField = groupBy === 'login' ? 'lastLogin' : 'registrationDate';
      
      // Create groups using a Map to preserve order and avoid duplicates
      const groupsMap = new Map<string, { label: string; users: any[] }>();
      
      // Sort users by the grouping field (most recent first)
      const sorted = [...filteredUsers].sort((a, b) => {
        const dateA = new Date(a[dateField] || 0).getTime();
        const dateB = new Date(b[dateField] || 0).getTime();
        return dateB - dateA;
      });

      // Group users by date
      sorted.forEach(user => {
        const date = toPacificDate(user[dateField]);
        if (date) {
          const { key, label } = getGroupInfo(date);
          
          if (!groupsMap.has(key)) {
            groupsMap.set(key, { label, users: [] });
          }
          groupsMap.get(key)!.users.push(user);
        } else {
          // Handle users with no date (e.g., no login yet for unverified users)
          const key = '999-no-date';
          const label = dateField === 'lastLogin' ? 'No sign-in yet' : 'No Registration Date';
          
          if (!groupsMap.has(key)) {
            groupsMap.set(key, { label, users: [] });
          }
          groupsMap.get(key)!.users.push(user);
        }
      });

      // Convert map to array for rendering
      groupedUsersArray = Array.from(groupsMap.entries()).map(([key, value]) => ({
        key,
        label: value.label,
        users: value.users
      }));
    }

    return (
      <div className={styles.tabContent}>
        <div className={styles.tabHeader}>
          <h2>User Management</h2>
        </div>
        
        {/* Filter Controls */}
        <div className={styles.filtersContainer}>
          <div className={styles.filterRow}>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Group by:</label>
              <select 
                value={groupBy} 
                onChange={(e) => setGroupBy(e.target.value as any)}
                className={styles.filterSelect}
              >
                <option value="login">Sign-in date</option>
                <option value="registration">Reg Date</option>
                <option value="nothing">Nothing</option>
              </select>
            </div>
            
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Status:</label>
              <select 
                value={statusFilter} 
                onChange={(e) => setStatusFilter(e.target.value as any)}
                className={styles.filterSelect}
              >
                <option value="active_verified">Active and Verified</option>
                <option value="not_verified">Not Verified</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            
            <div className={styles.userCount}>
              {filteredUsers.length} {filteredUsers.length === 1 ? 'user' : 'users'}
            </div>
          </div>
        </div>
        
        {error && <div className={styles.error}>{error}</div>}
        
        {loading ? (
          <div className={styles.loading}>Loading users...</div>
        ) : filteredUsers.length === 0 ? (
          <div className={styles.emptyState}>
            No users match the selected filters.
          </div>
        ) : groupBy === 'nothing' ? (
          <div className={styles.usersList}>
            {sortedUsers.map((user) => (
            <div key={user.email} className={`${styles.userItem} ${styles.compactItem} ${styles[`status-${user.status || 'active'}`]}`}>
              <div className={styles.userInfo}>
                <div className={styles.userHeader}>
                  <div className={styles.userEmail}>
                    {user.email}
                    {!user.emailVerified && (
                      <span className={styles.unverifiedBadge}>⚠️</span>
                    )}
                    {!user.hasProfile && (
                      <span className={styles.incompleteBadge}>📝</span>
                    )}
                    {user.isAdmin && <span className={styles.adminBadge}>Admin</span>}
                  </div>
                  <div className={styles.userMeta}>
                    <span className={styles.userName}>{user.name || 'No name'}</span>
                    <span className={`${styles.statusBadge} ${styles[`status-${user.status || 'active'}`]}`}>
                      {(user.status || 'active').charAt(0).toUpperCase() + (user.status || 'active').slice(1)}
                    </span>
                  </div>
                </div>
                <div className={styles.userDatesWithActions}>
                  <div className={styles.userDates}>
                    <span>Reg: {formatDate(user.registrationDate)}</span>
                    <span>•</span>
                    <span>Sign-in: {formatDate(user.lastLogin)}</span>
                    {user.deactivated_at && (
                      <>
                        <span>•</span>
                        <span>Deactivated: {formatDate(user.deactivated_at)}</span>
                      </>
                    )}
                  </div>
                  <div className={styles.inlineActions}>
                    {(user.status || 'active') !== 'active' ? (
                      <button
                        onClick={() => handleUpdateUserStatus(user.email, 'active')}
                        className={`${styles.actionButton} ${styles.compactButton}`}
                        disabled={loading}
                      >
                        Activate
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          const reason = prompt('Reason for deactivation (optional):');
                          handleUpdateUserStatus(user.email, 'inactive', reason || undefined);
                        }}
                        className={`${styles.actionButton} ${styles.compactButton} ${styles.deactivateButton}`}
                        disabled={loading}
                      >
                        Deactivate
                      </button>
                    )}
                    {user.isAdmin ? (
                      <>
                        <button
                          onClick={() => {
                            if (window.confirm(`Remove admin privileges from ${user.email}?`)) {
                              handleUpdateUserRole(user.email, 'user');
                            }
                          }}
                          className={`${styles.actionButton} ${styles.compactButton} ${styles.removeAdminButton}`}
                          disabled={loading}
                        >
                          Remove Admin
                        </button>
                        <button
                          onClick={() => handleManageRights(user)}
                          className={`${styles.actionButton} ${styles.compactButton} ${styles.manageRightsButton}`}
                          disabled={loading}
                        >
                          Manage Rights
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => {
                          if (window.confirm(`Grant admin privileges to ${user.email}?`)) {
                            handleUpdateUserRole(user.email, 'admin');
                          }
                        }}
                        className={`${styles.actionButton} ${styles.compactButton} ${styles.makeAdminButton}`}
                        disabled={loading}
                      >
                        Make Admin
                      </button>
                    )}
                    {(user.status || 'active') !== 'active' && (
                      <button
                        onClick={() => handleDeleteUserClick(user.email, user.name)}
                        className={`${styles.actionButton} ${styles.compactButton} ${styles.deleteButton}`}
                        disabled={loading}
                      >
                        🗑️ Delete
                      </button>
                    )}
                  </div>
                </div>
                {user.deactivation_reason && (
                  <div className={styles.deactivationReason}>
                    Reason: {user.deactivation_reason}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        ) : (
          <div className={styles.usersList}>
            {groupedUsersArray.map(({ key, label, users: groupUsers }) => (
              <React.Fragment key={key}>
                <div className={styles.groupHeader}>
                  {label} ({groupUsers.length})
                </div>
                {groupUsers.map((user) => (
                  <div key={user.email} className={`${styles.userItem} ${styles.compactItem} ${styles[`status-${user.status || 'active'}`]}`}>
                    <div className={styles.userInfo}>
                      <div className={styles.userHeader}>
                        <div className={styles.userEmail}>
                          {user.email}
                          {!user.emailVerified && (
                            <span className={styles.unverifiedBadge}>⚠️</span>
                          )}
                          {!user.hasProfile && (
                            <span className={styles.incompleteBadge}>📝</span>
                          )}
                          {user.isAdmin && <span className={styles.adminBadge}>Admin</span>}
                        </div>
                        <div className={styles.userMeta}>
                          <span className={styles.userName}>{user.name || 'No name'}</span>
                          <span className={`${styles.statusBadge} ${styles[`status-${user.status || 'active'}`]}`}>
                            {(user.status || 'active').charAt(0).toUpperCase() + (user.status || 'active').slice(1)}
                          </span>
                        </div>
                      </div>
                      <div className={styles.userDatesWithActions}>
                        <div className={styles.userDates}>
                          <span>Reg: {formatDate(user.registrationDate)}</span>
                          <span>•</span>
                          <span>Sign-in: {formatDate(user.lastLogin)}</span>
                          {user.deactivated_at && (
                            <>
                              <span>•</span>
                              <span>Deactivated: {formatDate(user.deactivated_at)}</span>
                            </>
                          )}
                        </div>
                        <div className={styles.inlineActions}>
                          {(user.status || 'active') !== 'active' ? (
                            <button
                              onClick={() => handleUpdateUserStatus(user.email, 'active')}
                              className={`${styles.actionButton} ${styles.compactButton}`}
                              disabled={loading}
                            >
                              Activate
                            </button>
                          ) : (
                            <button
                              onClick={() => {
                                const reason = prompt('Reason for deactivation (optional):');
                                handleUpdateUserStatus(user.email, 'inactive', reason || undefined);
                              }}
                              className={`${styles.actionButton} ${styles.compactButton} ${styles.deactivateButton}`}
                              disabled={loading}
                            >
                              Deactivate
                            </button>
                          )}
                          {user.isAdmin ? (
                            <>
                              <button
                                onClick={() => {
                                  if (window.confirm(`Remove admin privileges from ${user.email}?`)) {
                                    handleUpdateUserRole(user.email, 'user');
                                  }
                                }}
                                className={`${styles.actionButton} ${styles.compactButton} ${styles.removeAdminButton}`}
                                disabled={loading}
                              >
                                Remove Admin
                              </button>
                              <button
                                onClick={() => handleManageRights(user)}
                                className={`${styles.actionButton} ${styles.compactButton} ${styles.manageRightsButton}`}
                                disabled={loading}
                              >
                                Manage Rights
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => {
                                if (window.confirm(`Grant admin privileges to ${user.email}?`)) {
                                  handleUpdateUserRole(user.email, 'admin');
                                }
                              }}
                              className={`${styles.actionButton} ${styles.compactButton} ${styles.makeAdminButton}`}
                              disabled={loading}
                            >
                              Make Admin
                            </button>
                          )}
                          {(user.status || 'active') !== 'active' && (
                            <button
                              onClick={() => handleDeleteUserClick(user.email, user.name)}
                              className={`${styles.actionButton} ${styles.compactButton} ${styles.deleteButton}`}
                              disabled={loading}
                            >
                              🗑️ Delete
                            </button>
                          )}
                        </div>
                      </div>
                      {user.deactivation_reason && (
                        <div className={styles.deactivationReason}>
                          Reason: {user.deactivation_reason}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderHomeTab = () => (
    <div className={styles.tabContent}>
      <section className={styles.homePageConfigSection}>
        <p className={styles.homePageConfigNote}>Recovery Commons header is fixed. Edit tagline, heading, and bullets shown on the root home page.</p>
        {siteConfigLoading ? (
          <p>Loading...</p>
        ) : (
          <>
            <div className={styles.homePageConfigFields}>
              <label>
                <span>Tagline</span>
                <input
                  type="text"
                  value={siteConfig.tagline}
                  onChange={(e) => setSiteConfig((c) => ({ ...c, tagline: e.target.value }))}
                  className={styles.homePageConfigInput}
                />
              </label>
              <label>
                <span>Heading (between tagline and bullets)</span>
                <input
                  type="text"
                  value={siteConfig.section_heading}
                  onChange={(e) => setSiteConfig((c) => ({ ...c, section_heading: e.target.value }))}
                  className={styles.homePageConfigInput}
                />
              </label>
              <label>
                <span>Bullet 1</span>
                <input
                  type="text"
                  value={siteConfig.bullet1}
                  onChange={(e) => setSiteConfig((c) => ({ ...c, bullet1: e.target.value }))}
                  className={styles.homePageConfigInput}
                />
              </label>
              <label>
                <span>Bullet 2</span>
                <input
                  type="text"
                  value={siteConfig.bullet2}
                  onChange={(e) => setSiteConfig((c) => ({ ...c, bullet2: e.target.value }))}
                  className={styles.homePageConfigInput}
                />
              </label>
              <label>
                <span>Bullet 3</span>
                <input
                  type="text"
                  value={siteConfig.bullet3}
                  onChange={(e) => setSiteConfig((c) => ({ ...c, bullet3: e.target.value }))}
                  className={styles.homePageConfigInput}
                />
              </label>
              <label className={styles.homePageConfigCheckbox}>
                <input
                  type="checkbox"
                  checked={siteConfig.meetings_enabled}
                  onChange={(e) => setSiteConfig((c) => ({ ...c, meetings_enabled: e.target.checked }))}
                />
                <span>Meetings tab shows new experience (uncheck for &quot;Under construction&quot;)</span>
              </label>
            </div>
            <div className={styles.homePageConfigActions}>
              <button type="button" onClick={handleSaveSiteConfig} disabled={siteConfigSaving} className={styles.actionButton}>
                {siteConfigSaving ? 'Saving...' : 'Save home page'}
              </button>
              {siteConfigMessage && <span className={styles.homePageConfigMessage}>{siteConfigMessage}</span>}
            </div>
          </>
        )}
      </section>
    </div>
  );

  const renderProgramsTab = () => {
    return (
      <div className={styles.tabContent}>
        <ProgramsAdmin user={user} />
      </div>
    );
  };

  return (
    <div className={styles.adminPage}>
      <Header 
        breadcrumbs={breadcrumbs}
        user={user}
        onLogout={onLogout}
        onLogin={onLogin}
      />
      
      <main className={styles.mainContent}>
        <div className={styles.adminHeader}>
          <h1>Admin Dashboard</h1>
          <p>Manage feedback, view metrics, and monitor users</p>
        </div>

        <div className={styles.tabContainer}>
          <div className={styles.tabNavigation}>
            <button
              className={`${styles.tabButton} ${activeTab === 'home' ? styles.active : ''}`}
              onClick={() => handleTabChange('home')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
              </svg>
              Home Page
            </button>
            <button
              className={`${styles.tabButton} ${activeTab === 'programs' ? styles.active : ''}`}
              onClick={() => handleTabChange('programs')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M4 6h16v2H4zm0 5h16v2H4zm0 5h16v2H4z"/>
              </svg>
              Programs
            </button>
            <button
              className={`${styles.tabButton} ${activeTab === 'feedback' ? styles.active : ''}`}
              onClick={() => handleTabChange('feedback')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"/>
              </svg>
              Feedback
            </button>
            <button
              className={`${styles.tabButton} ${activeTab === 'users' ? styles.active : ''}`}
              onClick={() => handleTabChange('users')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
              </svg>
              Users
            </button>
            <button
              className={`${styles.tabButton} ${activeTab === 'metrics' ? styles.active : ''}`}
              onClick={() => handleTabChange('metrics')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/>
              </svg>
              Metrics
            </button>
          </div>

          <div className={styles.tabContent}>
            {activeTab === 'home' && renderHomeTab()}
            {activeTab === 'programs' && renderProgramsTab()}
            {activeTab === 'feedback' && renderFeedbackTab()}
            {activeTab === 'users' && renderUsersTab()}
            {activeTab === 'metrics' && renderMetricsTab()}
          </div>
        </div>

      </main>

      {/* Delete User Confirmation Modal */}
      <DeleteUserConfirmationModal
        isOpen={deleteModalOpen}
        onClose={handleDeleteUserCancel}
        onConfirm={handleDeleteUserConfirm}
        userEmail={userToDelete?.email || ''}
        userName={userToDelete?.name}
      />

      {/* Admin Rights Manager Modal */}
      {showRightsManager && userForRightsEdit && (
        <div className={styles.modalOverlay} onClick={handleRightsCancel}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <AdminRightsManager
              userEmail={userForRightsEdit.email}
              userName={userForRightsEdit.name}
              currentRole={userForRightsEdit.role}
              currentRights={userForRightsEdit.admin_rights || []}
              onSave={handleRightsSave}
              onCancel={handleRightsCancel}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin; 